import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { Link } from "react-router";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { EvidencePackPanel } from "@/components/evidence-pack";
import { PageHeader } from "@/components/bespoke";
import { Card } from "@/components/ui/card";
import { ApiError, runsQuery } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useCurrentProject } from "@/lib/project";
import { InstructionsSection } from "./instructions-section";
import { ProjectFilesSection } from "./project-files-section";

/**
 * The minimal Artifacts section in Project settings until the full artifact
 * catalogue lands: the latest runs with their signed evidence packs.
 * Full results stay on each run and in Evals.
 */
export function ArtifactsPage({ headingLevel = "h1" }: { headingLevel?: "h1" | "h2" } = {}) {
  const { project, loaded } = useCurrentProject();
  const runs = useQuery({
    ...runsQuery(project?.id ?? "", "all", 1),
    enabled: loaded && project !== null,
  });
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const heading = <PageHeader title="Artifacts" headingLevel={headingLevel} />;

  if (!loaded || (project !== null && runs.isPending)) {
    return (
      <>
        {heading}
        <LoadingState label="Loading artefacts" rows={4} />
      </>
    );
  }

  if (project === null) {
    return (
      <>
        {heading}
        <EmptyState
          title="No projects yet"
          description="Create your first project. Its evidence packs appear here."
        />
      </>
    );
  }

  if (runs.isError) {
    const error = runs.error;
    return (
      <>
        {heading}
        {error instanceof ApiError && error.status === 403 ? (
          <ForbiddenState
            title="You do not have access to this project"
            description="Ask an owner to invite you. Nothing from the project is shown."
          />
        ) : (
          <ErrorState
            title="Artefacts could not be loaded"
            description={
              error instanceof ApiError
                ? (error.problem?.detail ?? error.message)
                : "The request could not be completed."
            }
            requestId={error instanceof ApiError ? (error.requestId ?? "unknown") : "unknown"}
            onRetry={() => void runs.refetch()}
          />
        )}
      </>
    );
  }

  const list = runs.data?.runs ?? [];
  if (list.length === 0) {
    return (
      <>
        {heading}
        <EmptyState
          title="No evidence packs yet"
          description="Start a run. Its signed evidence pack appears here."
        />
      </>
    );
  }

  const selected = list.find((run) => run.id === selectedId) ?? list[0]!;

  return (
    <>
      {heading}
      <InstructionsSection
        projectId={project.id}
        canEdit={project.role === "owner" || project.role === "developer"}
      />
      <ProjectFilesSection
        projectId={project.id}
        canEdit={project.role === "owner" || project.role === "developer"}
      />
      <Card>
        <div className="border-b px-5 py-3">
          <h2 className="text-sm font-medium">Evidence packs</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Signed proof per run. Full results stay on each run.
          </p>
        </div>
        <ul className="divide-y">
          {list.map((run) => (
            <li key={run.id}>
              <div className="flex items-center justify-between gap-3 px-5 py-3">
                <button
                  type="button"
                  onClick={() => setSelectedId(run.id)}
                  aria-pressed={selected.id === run.id}
                  className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left text-sm hover:bg-muted/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">
                      {run.workItemTitle ?? `run_${run.id.slice(0, 6)}`}
                    </span>
                    <span className="block text-xs text-muted-foreground tabular-nums">
                      {formatDate(run.startedAt)} · {run.flowVersion}
                    </span>
                  </span>
                </button>
                <Link
                  to={`/runs/${run.id}`}
                  aria-label={`Open run ${run.workItemTitle ?? `run_${run.id.slice(0, 6)}`}`}
                  className="shrink-0 rounded-sm font-mono text-accent-text underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  Open run
                </Link>
              </div>
            </li>
          ))}
        </ul>
      </Card>
      <EvidencePackPanel projectId={project.id} runId={selected.id} />
    </>
  );
}
